import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';
import { useAnalytics } from '../context/AnalyticsContext';

const { FiArrowLeft, FiArrowRight, FiCheck, FiSave, FiShare2, FiPrinter, FiMail, FiTool, FiShield } = FiIcons;

const BuildPrice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getModelData } = useInventory();
  const { trackEvent } = useAnalytics();
  const allModels = getModelData();
  const model = id ? allModels.find(m => m.id === id) : allModels[0];
  
  const [step, setStep] = useState(1);
  const [configuration, setConfiguration] = useState({
    model: model?.id || '',
    trim: '',
    exterior: '',
    wheels: '',
    interior: '',
    packages: [],
    accessories: [],
    summary: {
      basePrice: model?.startingPrice || 0,
      trimPrice: 0,
      exteriorPrice: 0,
      wheelsPrice: 0,
      interiorPrice: 0,
      packagesPrice: 0,
      accessoriesPrice: 0
    }
  });
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Available configuration options
  const trims = [
    { id: 'standard', name: 'Standard', price: 0, description: 'The essential Audi experience with premium features' },
    { id: 'premium', name: 'Premium', price: 3500, description: 'Enhanced comfort and technology features' },
    { id: 'premium-plus', name: 'Premium Plus', price: 7000, description: 'Additional luxury features and advanced technology' },
    { id: 'prestige', name: 'Prestige', price: 12000, description: 'The ultimate expression of Audi luxury and innovation' }
  ];
  
  const exteriorColors = [
    { id: 'mythos-black', name: 'Mythos Black Metallic', price: 0, hex: '#000000', image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&h=600&fit=crop' },
    { id: 'glacier-white', name: 'Glacier White Metallic', price: 0, hex: '#FFFFFF', image: 'https://images.unsplash.com/photo-1617469767053-d3b16ee6a4ab?w=800&h=600&fit=crop' },
    { id: 'daytona-gray', name: 'Daytona Gray Pearl', price: 1200, hex: '#636363', image: 'https://images.unsplash.com/photo-1606220588913-b3aacb4d2f37?w=800&h=600&fit=crop' },
    { id: 'navarra-blue', name: 'Navarra Blue Metallic', price: 1200, hex: '#1C3E6C', image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&h=600&fit=crop' },
    { id: 'tango-red', name: 'Tango Red Metallic', price: 1200, hex: '#AF1E2D', image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&h=600&fit=crop' }
  ];
  
  const wheels = [
    { id: 'wheel-1', name: '18" 5-Arm Design', price: 0, image: 'https://images.unsplash.com/photo-1611921059084-64cca8cb9289?w=800&h=600&fit=crop' },
    { id: 'wheel-2', name: '19" 5-V-Spoke Design', price: 800, image: 'https://images.unsplash.com/photo-1611921059084-64cca8cb9289?w=800&h=600&fit=crop' },
    { id: 'wheel-3', name: '20" 5-Spoke Star Design', price: 1600, image: 'https://images.unsplash.com/photo-1611921059084-64cca8cb9289?w=800&h=600&fit=crop' },
    { id: 'wheel-4', name: '21" 5-Arm Polygon Design', price: 2400, image: 'https://images.unsplash.com/photo-1611921059084-64cca8cb9289?w=800&h=600&fit=crop' }
  ];
  
  const interiors = [
    { id: 'interior-1', name: 'Black Leather', price: 0, image: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=800&h=600&fit=crop' },
    { id: 'interior-2', name: 'Rock Gray Leather', price: 0, image: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=800&h=600&fit=crop' },
    { id: 'interior-3', name: 'Okapi Brown Leather', price: 1000, image: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=800&h=600&fit=crop' },
    { id: 'interior-4', name: 'Magma Red Leather', price: 1500, image: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=800&h=600&fit=crop' }
  ];
  
  const packages = [
    { id: 'package-1', name: 'Technology Package', price: 2500, description: 'Audi virtual cockpit, MMI Navigation plus, Audi connect PRIME & PLUS' },
    { id: 'package-2', name: 'S Line Sport Package', price: 1800, description: 'S line exterior, sport suspension, front sport seats' },
    { id: 'package-3', name: 'Convenience Package', price: 1500, description: 'Audi advanced key, power-folding mirrors, heated front seats' },
    { id: 'package-4', name: 'Driver Assistance Package', price: 1900, description: 'Adaptive cruise control, Audi active lane assist, traffic sign recognition' },
    { id: 'package-5', name: 'Bang & Olufsen Sound System', price: 950, description: '3D Premium Sound System with 19 speakers and 755 watts' },
    { id: 'package-6', name: 'Cold Weather Package', price: 850, description: 'Heated steering wheel, heated rear seats, all-weather floor mats' }
  ];
  
  const accessories = [
    { id: 'accessory-1', name: 'Roof Rack Bars', price: 450, description: 'Aluminum cross bars for mounting carrier accessories' },
    { id: 'accessory-2', name: 'All-Weather Floor Mats', price: 200, description: 'Heavy-duty rubber mats to protect your interior' },
    { id: 'accessory-3', name: 'Cargo Box', price: 850, description: 'Lockable 16 cubic foot roof-mounted cargo box' },
    { id: 'accessory-4', name: 'Bike Rack', price: 250, description: 'Roof-mounted bike carrier for one bike' },
    { id: 'accessory-5', name: 'Wheel Lock Kit', price: 150, description: 'Theft-deterrent wheel locks for your Audi' },
    { id: 'accessory-6', name: 'Cargo Liner', price: 180, description: 'Custom-fit liner to protect your cargo area' },
    { id: 'accessory-7', name: 'First Aid Kit', price: 45, description: 'Compact first aid kit in Audi case' },
    { id: 'accessory-8', name: 'LED Interior Lighting', price: 250, description: 'Enhanced LED interior lighting package' }
  ];
  
  // Update model when changed
  useEffect(() => {
    if (model) {
      setConfiguration(prev => ({
        ...prev,
        model: model.id,
        summary: {
          ...prev.summary,
          basePrice: model.startingPrice
        }
      }));
    }
  }, [model]);
  
  // Calculate total price
  const calculateTotal = () => {
    const { basePrice, trimPrice, exteriorPrice, wheelsPrice, interiorPrice, packagesPrice, accessoriesPrice } = configuration.summary;
    return basePrice + trimPrice + exteriorPrice + wheelsPrice + interiorPrice + packagesPrice + accessoriesPrice;
  };
  
  // Handle trim selection
  const handleTrimSelect = (trim) => {
    setConfiguration({
      ...configuration,
      trim: trim.id,
      summary: {
        ...configuration.summary,
        trimPrice: trim.price
      }
    });
    trackEvent('build_selection', { type: 'trim', selection: trim.name, price: trim.price });
  };
  
  // Handle exterior color selection
  const handleExteriorSelect = (color) => {
    setConfiguration({
      ...configuration,
      exterior: color.id,
      summary: {
        ...configuration.summary,
        exteriorPrice: color.price
      }
    });
    trackEvent('build_selection', { type: 'exterior', selection: color.name, price: color.price });
  };
  
  // Handle wheel selection
  const handleWheelSelect = (wheel) => {
    setConfiguration({
      ...configuration,
      wheels: wheel.id,
      summary: {
        ...configuration.summary,
        wheelsPrice: wheel.price
      }
    });
    trackEvent('build_selection', { type: 'wheels', selection: wheel.name, price: wheel.price });
  };
  
  // Handle interior selection
  const handleInteriorSelect = (interior) => {
    setConfiguration({
      ...configuration,
      interior: interior.id,
      summary: {
        ...configuration.summary,
        interiorPrice: interior.price
      }
    });
    trackEvent('build_selection', { type: 'interior', selection: interior.name, price: interior.price });
  };
  
  // Handle package selection
  const handlePackageToggle = (pkg) => {
    const updatedPackages = configuration.packages.includes(pkg.id)
      ? configuration.packages.filter(id => id !== pkg.id)
      : [...configuration.packages, pkg.id];
    
    const selectedPackages = packages.filter(p => updatedPackages.includes(p.id));
    const packagesPrice = selectedPackages.reduce((sum, p) => sum + p.price, 0);
    
    setConfiguration({
      ...configuration,
      packages: updatedPackages,
      summary: {
        ...configuration.summary,
        packagesPrice
      }
    });
    
    trackEvent('build_selection', { 
      type: 'package', 
      selection: pkg.name, 
      price: pkg.price,
      action: configuration.packages.includes(pkg.id) ? 'remove' : 'add'
    });
  };
  
  // Handle accessory selection
  const handleAccessoryToggle = (accessory) => {
    const updatedAccessories = configuration.accessories.includes(accessory.id)
      ? configuration.accessories.filter(id => id !== accessory.id)
      : [...configuration.accessories, accessory.id];
    
    const selectedAccessories = accessories.filter(a => updatedAccessories.includes(a.id));
    const accessoriesPrice = selectedAccessories.reduce((sum, a) => sum + a.price, 0);
    
    setConfiguration({
      ...configuration,
      accessories: updatedAccessories,
      summary: {
        ...configuration.summary,
        accessoriesPrice
      }
    });
    
    trackEvent('build_selection', { 
      type: 'accessory', 
      selection: accessory.name, 
      price: accessory.price,
      action: configuration.accessories.includes(accessory.id) ? 'remove' : 'add'
    });
  };
  
  // Handle navigation between steps
  const goToStep = (newStep) => {
    if (newStep >= 1 && newStep <= 6) {
      setStep(newStep);
      trackEvent('build_progress', { step: newStep, model: model?.name });
      window.scrollTo(0, 0);
    }
  };
  
  // Handle model change
  const handleModelChange = (modelId) => {
    const selectedModel = allModels.find(m => m.id === modelId);
    if (selectedModel) {
      navigate(`/build-your-audi/${modelId}`);
    }
  };
  
  // Handle share submission
  const handleShareSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      trackEvent('build_share', { model: model?.name, email });
      
      setTimeout(() => {
        setShowShareModal(false);
        setEmail('');
        setSubmitSuccess(false);
      }, 3000);
    }, 1500);
  };
  
  // Handle print
  const handlePrint = () => {
    window.print();
    trackEvent('build_print', { model: model?.name });
  };
  
  // Handle save configuration
  const handleSaveConfiguration = () => {
    const configData = JSON.stringify(configuration);
    localStorage.setItem('audiConfiguration', configData);
    alert('Configuration saved successfully!');
    trackEvent('build_save', { model: model?.name });
  };
  
  // If no model is found, redirect to models page
  if (!model) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-4">Model Not Found</h1>
          <Link to="/models" className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors">
            Browse Models
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Header */}
      <section className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <Link to="/models" className="inline-flex items-center text-gray-300 hover:text-white mb-4 md:mb-0">
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
                <span>Back to Models</span>
              </Link>
              <h1 className="text-3xl md:text-4xl font-light uppercase tracking-tight">
                Build Your {model.name}
              </h1>
            </div>
            <div className="flex flex-col items-end">
              <div className="text-xl font-bold">
                ${calculateTotal().toLocaleString()}
              </div>
              <div className="text-sm text-gray-300">
                As configured
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Progress Steps */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 space-x-4">
            {[
              { step: 1, label: 'Model' },
              { step: 2, label: 'Trim' },
              { step: 3, label: 'Exterior' },
              { step: 4, label: 'Interior' },
              { step: 5, label: 'Packages' },
              { step: 6, label: 'Accessories' }
            ].map((s) => (
              <button
                key={s.step}
                onClick={() => goToStep(s.step)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium ${
                  step === s.step
                    ? 'bg-red-600 text-white'
                    : step > s.step
                    ? 'bg-gray-200 text-gray-800'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {s.step}. {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Model Selection */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Select Your Audi Model
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {allModels.map((m, index) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    configuration.model === m.id ? 'border-red-600' : 'border-transparent'
                  } hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => handleModelChange(m.id)}
                >
                  <div className="relative h-48">
                    <img 
                      src={m.image} 
                      alt={`Audi ${m.name}`} 
                      className="w-full h-full object-cover" 
                    />
                    {configuration.model === m.id && (
                      <div className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full">
                        <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-medium text-gray-900 mb-2">
                      Audi {m.name}
                    </h3>
                    <p className="text-red-600 font-semibold mb-4">
                      Starting at ${m.startingPrice.toLocaleString()}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {m.highlights.slice(0, 2).map((highlight, idx) => (
                        <div key={idx} className="flex flex-col">
                          <span className="text-xs text-gray-500">{highlight.title}</span>
                          <span className="text-sm font-medium">{highlight.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="flex justify-end pt-6">
              <button
                onClick={() => goToStep(2)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <span>Continue to Trim</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Step 2: Trim Selection */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Select Your Trim
            </h2>
            
            <div className="space-y-4">
              {trims.map((trim, index) => (
                <motion.div
                  key={trim.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    configuration.trim === trim.id ? 'border-red-600' : 'border-transparent'
                  } hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => handleTrimSelect(trim)}
                >
                  <div className="p-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-xl font-medium text-gray-900 mr-2">
                          {trim.name}
                        </h3>
                        {configuration.trim === trim.id && (
                          <span className="bg-red-600 text-white p-1 rounded-full">
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4 md:mb-0">
                        {trim.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-600 font-semibold">
                        {trim.price === 0 ? 'Included' : `+$${trim.price.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="flex justify-between pt-6">
              <button
                onClick={() => goToStep(1)}
                className="border-2 border-black text-black px-8 py-3 font-medium hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => goToStep(3)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
                disabled={!configuration.trim}
              >
                <span>Continue to Exterior</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Step 3: Exterior Selection */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Select Your Exterior Color
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <img 
                    src={exteriorColors.find(c => c.id === configuration.exterior)?.image || model.image} 
                    alt={`${model.name} exterior`}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              </div>
              
              {exteriorColors.map((color, index) => (
                <motion.div
                  key={color.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    configuration.exterior === color.id ? 'border-red-600' : 'border-transparent'
                  } hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => handleExteriorSelect(color)}
                >
                  <div className="p-6 flex items-center">
                    <div 
                      className="w-12 h-12 rounded-full mr-4 border border-gray-300" 
                      style={{ backgroundColor: color.hex }}
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1 flex items-center">
                        {color.name}
                        {configuration.exterior === color.id && (
                          <span className="ml-2 text-red-600">
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </span>
                        )}
                      </h3>
                      <p className="text-red-600 font-medium">
                        {color.price === 0 ? 'Included' : `+$${color.price.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="flex justify-between pt-6">
              <button
                onClick={() => goToStep(2)}
                className="border-2 border-black text-black px-8 py-3 font-medium hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => goToStep(4)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
                disabled={!configuration.exterior}
              >
                <span>Continue to Interior</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Step 4: Interior Selection */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-2 md:mb-0 uppercase tracking-tight">
                Select Your Interior
              </h2>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Selected Wheels:</span>
                <button
                  onClick={() => goToStep(3)}
                  className="text-red-600 hover:text-red-700 transition-colors inline-flex items-center space-x-1"
                >
                  <span>Change Wheels</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <img 
                      src={wheels.find(w => w.id === configuration.wheels)?.image || wheels[0].image} 
                      alt="Wheels"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <img 
                      src={interiors.find(i => i.id === configuration.interior)?.image || interiors[0].image}
                      alt="Interior"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-3">
                <h3 className="text-xl font-light text-gray-900 mb-4 uppercase tracking-tight">
                  Wheels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  {wheels.map((wheel, index) => (
                    <motion.div
                      key={wheel.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                        configuration.wheels === wheel.id ? 'border-red-600' : 'border-transparent'
                      } hover:shadow-lg transition-all cursor-pointer`}
                      onClick={() => handleWheelSelect(wheel)}
                    >
                      <div className="p-4 text-center">
                        <div className="h-24 flex items-center justify-center mb-2">
                          <img 
                            src={wheel.image} 
                            alt={wheel.name}
                            className="h-full object-contain"
                          />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center justify-center">
                          {wheel.name}
                          {configuration.wheels === wheel.id && (
                            <span className="ml-2 text-red-600">
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                            </span>
                          )}
                        </h4>
                        <p className="text-red-600 text-sm font-medium">
                          {wheel.price === 0 ? 'Included' : `+$${wheel.price.toLocaleString()}`}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="md:col-span-3">
                <h3 className="text-xl font-light text-gray-900 mb-4 uppercase tracking-tight">
                  Interior Options
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {interiors.map((interior, index) => (
                    <motion.div
                      key={interior.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                        configuration.interior === interior.id ? 'border-red-600' : 'border-transparent'
                      } hover:shadow-lg transition-all cursor-pointer`}
                      onClick={() => handleInteriorSelect(interior)}
                    >
                      <div className="relative h-32">
                        <img 
                          src={interior.image} 
                          alt={interior.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center">
                          {interior.name}
                          {configuration.interior === interior.id && (
                            <span className="ml-2 text-red-600">
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                            </span>
                          )}
                        </h4>
                        <p className="text-red-600 text-sm font-medium">
                          {interior.price === 0 ? 'Included' : `+$${interior.price.toLocaleString()}`}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between pt-6">
              <button
                onClick={() => goToStep(3)}
                className="border-2 border-black text-black px-8 py-3 font-medium hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => goToStep(5)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
                disabled={!configuration.wheels || !configuration.interior}
              >
                <span>Continue to Packages</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Step 5: Packages Selection */}
        {step === 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Select Your Packages
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    configuration.packages.includes(pkg.id) ? 'border-red-600' : 'border-transparent'
                  } hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => handlePackageToggle(pkg)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-medium text-gray-900 flex items-center">
                        {pkg.name}
                        {configuration.packages.includes(pkg.id) && (
                          <span className="ml-2 bg-red-600 text-white p-1 rounded-full">
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </span>
                        )}
                      </h3>
                      <p className="text-red-600 font-semibold">
                        +${pkg.price.toLocaleString()}
                      </p>
                    </div>
                    <p className="text-gray-600">
                      {pkg.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="flex justify-between pt-6">
              <button
                onClick={() => goToStep(4)}
                className="border-2 border-black text-black px-8 py-3 font-medium hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => goToStep(6)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <span>Continue to Accessories</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Step 6: Accessories Selection */}
        {step === 6 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Select Your Accessories
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {accessories.map((accessory, index) => (
                <motion.div
                  key={accessory.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    configuration.accessories.includes(accessory.id) ? 'border-red-600' : 'border-transparent'
                  } hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => handleAccessoryToggle(accessory)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        {accessory.name}
                        {configuration.accessories.includes(accessory.id) && (
                          <span className="ml-2 bg-red-600 text-white p-1 rounded-full">
                            <SafeIcon icon={FiCheck} className="w-3 h-3" />
                          </span>
                        )}
                      </h3>
                      <p className="text-red-600 font-semibold">
                        +${accessory.price}
                      </p>
                    </div>
                    <p className="text-gray-600 text-sm">
                      {accessory.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="flex justify-between pt-6">
              <button
                onClick={() => goToStep(5)}
                className="border-2 border-black text-black px-8 py-3 font-medium hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => setShowSummaryModal(true)}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
              >
                <span>View Summary</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 space-y-3">
        <button
          onClick={handleSaveConfiguration}
          className="bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-colors"
          title="Save Configuration"
        >
          <SafeIcon icon={FiSave} className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowShareModal(true)}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          title="Share Configuration"
        >
          <SafeIcon icon={FiShare2} className="w-5 h-5" />
        </button>
        <button
          onClick={handlePrint}
          className="bg-green-600 text-white p-3 rounded-full shadow-lg hover:bg-green-700 transition-colors"
          title="Print Configuration"
        >
          <SafeIcon icon={FiPrinter} className="w-5 h-5" />
        </button>
      </div>
      
      {/* Summary Modal */}
      {showSummaryModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Configuration Summary</h3>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b">
                <span className="font-medium">Base Price ({model.name})</span>
                <span className="font-semibold">${configuration.summary.basePrice.toLocaleString()}</span>
              </div>
              
              {configuration.summary.trimPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Trim ({trims.find(t => t.id === configuration.trim)?.name})</span>
                  <span className="font-semibold">+${configuration.summary.trimPrice.toLocaleString()}</span>
                </div>
              )}
              
              {configuration.summary.exteriorPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Exterior Color</span>
                  <span className="font-semibold">+${configuration.summary.exteriorPrice.toLocaleString()}</span>
                </div>
              )}
              
              {configuration.summary.wheelsPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Wheels</span>
                  <span className="font-semibold">+${configuration.summary.wheelsPrice.toLocaleString()}</span>
                </div>
              )}
              
              {configuration.summary.interiorPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Interior</span>
                  <span className="font-semibold">+${configuration.summary.interiorPrice.toLocaleString()}</span>
                </div>
              )}
              
              {configuration.summary.packagesPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Packages ({configuration.packages.length})</span>
                  <span className="font-semibold">+${configuration.summary.packagesPrice.toLocaleString()}</span>
                </div>
              )}
              
              {configuration.summary.accessoriesPrice > 0 && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-medium">Accessories ({configuration.accessories.length})</span>
                  <span className="font-semibold">+${configuration.summary.accessoriesPrice.toLocaleString()}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center py-4 bg-red-600 text-white px-4 rounded-lg">
                <span className="text-xl font-bold">Total Price</span>
                <span className="text-2xl font-bold">${calculateTotal().toLocaleString()}</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link
                to={`/financing?vehicle=${model.id}&price=${calculateTotal()}`}
                className="flex-1 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
              >
                Get Financing
              </Link>
              <Link
                to="/contact"
                className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 transition-colors text-center uppercase tracking-wider text-sm"
              >
                Contact Dealer
              </Link>
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Share Modal */}
      {showShareModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            {submitSuccess ? (
              <div className="text-center py-6">
                <div className="bg-green-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Configuration Shared!
                </h3>
                <p className="text-gray-600">
                  Your Audi configuration has been sent to your email.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Share Your Configuration
                </h3>
                <form onSubmit={handleShareSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Configuration'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowShareModal(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default BuildPrice;